import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Container, Form, Row } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaExchangeAlt,
  FaList,
  FaRedoAlt,
  FaSearch,
} from 'react-icons/fa';
import AdminDataTable from './AdminDataTable';
import AdminPageHeader from './AdminPageHeader';
import Navigation from './Navigation';
import useAuth from '../../hooks/useAuth';
import { isAdminUser, readStoredAdminUser } from './adminAccessControl';
import './PosSyncMonitor.css';

const POS_API_BASE_URL = process.env.REACT_APP_API_PRODUCT_URL || '';

const isSuccessResponse = value => value === 0 || value === '0';

const formatCurrency = value =>
  `Rp ${Number(value || 0).toLocaleString('id-ID', {
    maximumFractionDigits: 0,
  })}`;

const formatDateTime = value => {
  if (!value) {
    return '-';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return parsed.toLocaleString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusClassName = status => {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'synced') {
    return '';
  }
  if (normalized === 'failed') {
    return 'is-danger';
  }
  return 'is-warning';
};

function PosSyncMonitor() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyAndRefreshToken } = useAuth();
  const canAccess = isAdminUser(readStoredAdminUser());
  const queryOutletId = useMemo(() => new URLSearchParams(location.search).get('outlet_id') || '', [location.search]);
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [retryingKey, setRetryingKey] = useState('');
  const [bulkRetrying, setBulkRetrying] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    recordType: '',
    outletId: queryOutletId,
    query: '',
  });

  useEffect(() => {
    if (!queryOutletId) {
      return;
    }

    setFilters(prev => (prev.outletId === queryOutletId ? prev : { ...prev, outletId: queryOutletId }));
  }, [queryOutletId]);

  const buildHeaders = token => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  });

  const fetchSummary = useCallback(async token => {
    setSummaryLoading(true);
    try {
      const query = new URLSearchParams();
      if (filters.outletId.trim()) {
        query.set('outlet_id', filters.outletId.trim());
      }

      const response = await fetch(
        `${POS_API_BASE_URL}api/accounting/sync/summary${query.toString() ? `?${query}` : ''}`,
        { headers: buildHeaders(token) }
      );
      const data = await response.json();

      if (!response.ok || !isSuccessResponse(data.error)) {
        throw new Error(data.message || data.error || 'Failed to fetch sync summary');
      }

      setSummary(data.data || null);
    } catch (fetchError) {
      console.error('Failed to fetch POS sync summary:', fetchError);
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [filters.outletId]);

  const fetchRecords = useCallback(async token => {
    setRecordsLoading(true);
    try {
      const query = new URLSearchParams();
      query.set('limit', '100');
      if (filters.status.trim()) {
        query.set('status', filters.status.trim());
      }
      if (filters.recordType.trim()) {
        query.set('record_type', filters.recordType.trim());
      }
      if (filters.outletId.trim()) {
        query.set('outlet_id', filters.outletId.trim());
      }
      if (filters.query.trim()) {
        query.set('q', filters.query.trim());
      }

      const response = await fetch(
        `${POS_API_BASE_URL}api/accounting/sync/records?${query.toString()}`,
        { headers: buildHeaders(token) }
      );
      const data = await response.json();

      if (!response.ok || !isSuccessResponse(data.error)) {
        throw new Error(data.message || data.error || 'Failed to fetch sync records');
      }

      setRecords(Array.isArray(data.records) ? data.records : []);
    } catch (fetchError) {
      console.error('Failed to fetch POS sync records:', fetchError);
      setRecords([]);
      Swal.fire('Error', 'Gagal mengambil daftar sync POS.', 'error');
    } finally {
      setRecordsLoading(false);
    }
  }, [filters]);

  const refreshData = useCallback(async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return;
    }

    const isValid = await verifyAndRefreshToken(token);
    if (!isValid) {
      navigate('/loginadmin');
      return;
    }

    const refreshedToken = localStorage.getItem('sessionToken');
    if (!refreshedToken) {
      navigate('/loginadmin');
      return;
    }

    await Promise.all([fetchSummary(refreshedToken), fetchRecords(refreshedToken)]);
  }, [fetchRecords, fetchSummary, navigate, verifyAndRefreshToken]);

  useEffect(() => {
    if (!canAccess) {
      navigate('/dashboardadmin', { replace: true });
      return;
    }

    if (hasInitialized) {
      return;
    }

    setHasInitialized(true);
    refreshData();
  }, [canAccess, hasInitialized, navigate, refreshData]);

  const handleFilterChange = event => {
    const { name, value } = event.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleRetryRecord = async record => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return;
    }

    const retryKey = `${record.record_type}-${record.record_id}`;
    setRetryingKey(retryKey);
    try {
      const response = await fetch(`${POS_API_BASE_URL}api/accounting/sync/retry`, {
        method: 'POST',
        headers: buildHeaders(token),
        body: JSON.stringify({
          record_type: record.record_type,
          id: record.record_id,
        }),
      });
      const data = await response.json();
      if (!response.ok || !isSuccessResponse(data.error)) {
        throw new Error(data.message || 'Retry sync failed');
      }

      await refreshData();
      Swal.fire('Success', 'Sync berhasil dicoba ulang.', 'success');
    } catch (retryError) {
      console.error('Retry sync failed:', retryError);
      Swal.fire('Error', retryError.message || 'Retry sync failed', 'error');
    } finally {
      setRetryingKey('');
    }
  };

  const handleRetryFailed = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return;
    }

    setBulkRetrying(true);
    try {
      const response = await fetch(`${POS_API_BASE_URL}api/accounting/sync/retry-failed`, {
        method: 'POST',
        headers: buildHeaders(token),
        body: JSON.stringify({
          record_type: filters.recordType.trim(),
          outlet_id: filters.outletId.trim() ? Number(filters.outletId.trim()) : 0,
          limit: 50,
        }),
      });
      const data = await response.json();
      if (!response.ok || !isSuccessResponse(data.error)) {
        throw new Error(data.message || 'Bulk retry failed');
      }

      await refreshData();
      const resultData = data.data || {};
      Swal.fire(
        'Bulk retry selesai',
        `${resultData.succeeded || 0} berhasil, ${resultData.failed || 0} gagal.`,
        'success'
      );
    } catch (retryError) {
      console.error('Bulk retry failed:', retryError);
      Swal.fire('Error', retryError.message || 'Bulk retry failed', 'error');
    } finally {
      setBulkRetrying(false);
    }
  };

  const columns = [
    {
      name: 'Source',
      sortable: true,
      grow: 1.4,
      cell: row => (
        <div className="table-identity">
          <span className="table-identity__avatar">
            {String(row.record_type || '').slice(0, 2).toUpperCase() || 'PS'}
          </span>
          <span className="table-identity__meta">
            <strong>{String(row.record_type || '').toUpperCase()} #{row.record_id}</strong>
            <small>{row.external_reference}</small>
          </span>
        </div>
      ),
    },
    {
      name: 'Outlet',
      sortable: true,
      cell: row => (
        <span className="table-identity__meta">
          <strong>Outlet #{row.outlet_id}</strong>
          <small>{formatDateTime(row.occurred_at)}</small>
        </span>
      ),
    },
    {
      name: 'Amount',
      sortable: true,
      right: true,
      cell: row => (
        <span className="table-identity__meta">
          <strong>{formatCurrency(row.amount)}</strong>
          <small>{row.description || 'No description'}</small>
        </span>
      ),
    },
    {
      name: 'Status',
      sortable: true,
      cell: row => (
        <span className={`status-pill ${getStatusClassName(row.sync_status)}`}>
          {row.sync_status || 'pending'}
        </span>
      ),
    },
    {
      name: 'Sync Detail',
      grow: 1.6,
      cell: row => (
        <span className="table-identity__meta">
          <strong>{row.synced_at ? formatDateTime(row.synced_at) : 'Belum tersinkron'}</strong>
          <small className="sync-monitor-table-error">
            {row.sync_error || row.idempotency_key || 'Tidak ada catatan error.'}
          </small>
        </span>
      ),
    },
    {
      name: 'Action',
      cell: row => (
        <div className="table-action-group">
          <Button
            variant="outline-success"
            size="sm"
            disabled={row.sync_status === 'synced' || retryingKey === `${row.record_type}-${row.record_id}`}
            onClick={() => handleRetryRecord(row)}
          >
            <FaRedoAlt /> Retry
          </Button>
        </div>
      ),
    },
  ];

  const summaryCards = [
    {
      key: 'total',
      icon: FaList,
      label: 'Total Records',
      value: summary?.total ?? 0,
      description: 'Seluruh sale, refund, dan purchase yang dipantau.',
    },
    {
      key: 'synced',
      icon: FaCheckCircle,
      label: 'Synced',
      value: summary?.synced ?? 0,
      description: 'Sudah berhasil dipost ke ledger pusat.',
    },
    {
      key: 'pending',
      icon: FaClock,
      label: 'Pending',
      value: summary?.pending ?? 0,
      description: 'Masih menunggu atau belum selesai dipost.',
    },
    {
      key: 'failed',
      icon: FaExclamationTriangle,
      label: 'Failed',
      value: summary?.failed ?? 0,
      description: 'Perlu perhatian atau retry manual.',
    },
  ];

  return (
    <div>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4">
        <AdminPageHeader
          icon={FaExchangeAlt}
          eyebrow="Data sync"
          title="Sync Monitor"
          description="Pantau status pengiriman jurnal POS ke ledger pusat, cek error yang tertinggal, dan trigger retry langsung dari panel admin."
          breadcrumbs={[
            { label: 'Dashboard', to: '/dashboardadmin' },
            { label: 'Sync Monitor' },
          ]}
          meta={(
            <span className="soft-stat">
              <FaList /> {records.length} visible records
            </span>
          )}
          actions={(
            <>
              <Button variant="outline-secondary" onClick={refreshData}>
                <FaRedoAlt /> Refresh
              </Button>
              <Button variant="success" onClick={handleRetryFailed} disabled={bulkRetrying}>
                <FaExchangeAlt /> {bulkRetrying ? 'Retrying...' : 'Retry Failed'}
              </Button>
            </>
          )}
        />

        <div className="sync-monitor-banner">
          Posting sale POS sekarang tidak lagi bergantung pada fee outlet per transaksi. Fokus retry cukup pada mapping kas, penjualan, pajak, diskon, inventory, dan COGS.
        </div>

        <div className="sync-monitor-grid">
          {summaryCards.map(card => {
            const Icon = card.icon;
            return (
              <article key={card.key} className="sync-monitor-card">
                <span className="sync-monitor-card__icon">
                  <Icon />
                </span>
                <span className="sync-monitor-card__eyebrow">{card.label}</span>
                <strong>{summaryLoading ? '...' : Number(card.value).toLocaleString('id-ID')}</strong>
                <p>{card.description}</p>
              </article>
            );
          })}
        </div>

        <div className="sync-monitor-type-pills">
          {(summary?.by_record_type || []).map(item => (
            <div key={item.record_type} className="sync-monitor-type-pill">
              <strong>{String(item.record_type || '').toUpperCase()}</strong>
              <span>{item.total} total</span>
              <small>{item.failed} failed • {item.pending} pending</small>
            </div>
          ))}
        </div>

        <div className="sync-monitor-toolbar">
          <Row className="g-3 sync-monitor-toolbar__filters">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select name="status" value={filters.status} onChange={handleFilterChange}>
                  <option value="">All status</option>
                  <option value="failed">Failed</option>
                  <option value="pending">Pending</option>
                  <option value="synced">Synced</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Record Type</Form.Label>
                <Form.Select name="recordType" value={filters.recordType} onChange={handleFilterChange}>
                  <option value="">All types</option>
                  <option value="sale">Sale</option>
                  <option value="refund">Refund</option>
                  <option value="purchase">Purchase</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Outlet</Form.Label>
                <Form.Control
                  type="text"
                  name="outletId"
                  value={filters.outletId}
                  onChange={handleFilterChange}
                  placeholder="Outlet ID"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Search</Form.Label>
                <div className="sync-monitor-search">
                  <FaSearch />
                  <Form.Control
                    type="search"
                    name="query"
                    value={filters.query}
                    onChange={handleFilterChange}
                    placeholder="Reference atau description"
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
          <div className="sync-monitor-toolbar__actions">
            <Button variant="dark" onClick={refreshData}>
              Apply Filter
            </Button>
          </div>
        </div>

        <AdminDataTable
          columns={columns}
          data={records}
          progressPending={recordsLoading}
          pagination
          highlightOnHover
          emptyTitle="No sync records found"
          emptyDescription="Belum ada data sync POS yang cocok dengan filter saat ini."
        />
      </Container>
    </div>
  );
}

export default PosSyncMonitor;
